import { useMemo, useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, fromMajor, money, toMajor } from '@/lib/money'
import { cn } from '@/lib/cn'
import type { Id } from '@/data/types'
import styles from './SendAbroadScreen.module.css'

const SEND_FEE_RATE = 0.01

export function SendAbroadScreen() {
  const repository = useRepository()
  const corridors = useQuery(() => repository.listCorridors(), [repository])
  const recipients = useQuery(() => repository.listRecipients(), [repository])

  const [amount, setAmount] = useState('500')
  const [corridorId, setCorridorId] = useState<Id<'corridor'> | null>(null)

  const selectable = corridors.data?.filter((corridor) => corridor.currency !== 'USD') ?? []
  const corridor = selectable.find((item) => item.id === corridorId) ?? selectable[0]

  const quote = useMemo(() => {
    if (!corridor) return null
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const send = fromMajor(Number.isFinite(parsed) && parsed > 0 ? parsed : 0, 'USD')
    const fee = money(Math.round(send.minorUnits * SEND_FEE_RATE), 'USD')
    const rate = corridor.midRate * (1 - corridor.spread)
    const receive = fromMajor(toMajor(send) * rate, corridor.currency)
    const total = money(send.minorUnits + fee.minorUnits, 'USD')
    /* On a pegged corridor we deliver dollars, so what lands is the amount
       after our fee — not a local figure we cannot actually honour. */
    const net = money(send.minorUnits - fee.minorUnits, 'USD')
    return { send, fee, rate, receive, total, net }
  }, [amount, corridor])

  const matchingRecipients =
    recipients.data?.filter((recipient) => recipient.corridorId === corridor?.id) ?? []

  return (
    <>
      <PageHeader
        title="Send abroad"
        subtitle="One price, shown before you send. The rate is the mid-market rate minus our spread."
        actions={
          <Button variant="ghost" size="md" iconStart="history" to="/transfers">
            All transfers
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  You send
                </Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">
                    From your US Dollar account
                  </Text>
                  <div className={styles.amountRow}>
                    <CurrencyBadge currency="USD" size={36} />
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label="Amount to send in US dollars"
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">
                      USD
                    </Text>
                  </div>
                </div>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  Where it lands
                </Text>
                {corridors.status === 'loading' && <Skeleton height={96} />}
                <div className={styles.corridors}>
                  {selectable.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setCorridorId(item.id)}
                      className={cn(styles.corridor, item.id === corridor?.id && styles.corridorActive)}
                      aria-pressed={item.id === corridor?.id}
                    >
                      <CurrencyBadge currency={item.currency} size={28} />
                      <Stack gap={0}>
                        <Text variant="caption" weight="semibold" truncate>
                          {item.country}
                        </Text>
                        <Text variant="eyebrow" tone="secondary">
                          {item.rail}
                        </Text>
                      </Stack>
                    </button>
                  ))}
                </div>
              </Stack>

              {matchingRecipients.length > 0 && (
                <Stack gap={3}>
                  <Text variant="caption" weight="semibold">
                    Recipient
                  </Text>
                  {matchingRecipients.map((recipient) => (
                    <Stack key={recipient.id} direction="row" align="center" gap={3}>
                      <Icon name="person" size={20} />
                      <Stack grow gap={0}>
                        <Text variant="caption" weight="semibold">
                          {recipient.name}
                        </Text>
                        <Text variant="eyebrow" tone="secondary">
                          {recipient.maskedAccount}
                        </Text>
                      </Stack>
                      <Pill tone="neutral">Saved</Pill>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                They receive
              </Text>
              {corridor && <Pill tone="accent">{corridor.arrival}</Pill>}
            </Card.Header>

            {quote && corridor && (
              <>
                <Stack gap={1} className="pb-mt-4">
                  <Text as="p" variant="display" weight="bold" tone="accent" numeric>
                    {corridor.pegged
                      ? formatMoney(quote.net)
                      : formatMoney(quote.receive, { compactDecimals: true })}
                  </Text>
                  <Text variant="caption" tone="onDarkMuted" numeric>
                    {corridor.pegged
                      ? `in dollars · the official rate is ${corridor.midRate.toFixed(2)} ${corridor.currency}`
                      : `1 USD = ${quote.rate.toFixed(corridor.currency === 'COP' ? 0 : 2)} ${corridor.currency}`}
                  </Text>
                </Stack>

                {corridor.note && (
                  <Stack direction="row" align="start" gap={2} className="pb-mt-4">
                    <Icon name="info" size={16} />
                    <Text variant="caption" tone="onDarkMuted">
                      {corridor.note}
                    </Text>
                  </Stack>
                )}

                <div className={styles.rule} />

                <div className={styles.breakdown}>
                  <div className={styles.line}>
                    <Text variant="caption" tone="onDarkMuted">
                      Amount
                    </Text>
                    <Text variant="caption" weight="semibold" tone="onDark" numeric>
                      {formatMoney(quote.send)}
                    </Text>
                  </div>
                  <div className={styles.line}>
                    <Text variant="caption" tone="onDarkMuted">
                      Our fee · 1.0%
                    </Text>
                    <Text variant="caption" weight="semibold" tone="onDark" numeric>
                      {formatMoney(quote.fee)}
                    </Text>
                  </div>
                  <div className={styles.line}>
                    <Text variant="caption" tone="onDarkMuted">
                      {corridor.pegged
                        ? 'FX spread'
                        : `FX spread · ${(corridor.spread * 100).toFixed(1)}%`}
                    </Text>
                    <Text
                      variant="caption"
                      weight="semibold"
                      tone={corridor.pegged ? 'accent' : 'onDark'}
                    >
                      {corridor.pegged ? 'None · nothing is converted' : 'in the rate above'}
                    </Text>
                  </div>
                </div>

                <div className={styles.rule} />

                <div className={styles.line}>
                  <Text variant="caption" weight="semibold" tone="onDark">
                    Total to pay
                  </Text>
                  <Text variant="subtitle" weight="bold" tone="onDark" numeric>
                    {formatMoney(quote.total)}
                  </Text>
                </div>

                <Card.Footer>
                  <Button variant="primary" size="lg" block iconEnd="arrow_forward" to="/transfers/send/review">
                    Review transfer
                  </Button>
                  <Text variant="eyebrow" tone="onDarkMuted" className="pb-mt-3">
                    Nothing is charged until you confirm on the next screen.
                  </Text>
                </Card.Footer>
              </>
            )}
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
